import type React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "~/components/ui/button";
import ToggleSelector from "~/components/ui/paymentToggle";
import PlanListItem from "~/components/ui/planListItem";
import { TopSticky } from "~/components/ui/topSticky";
import { planMap } from "~/constants/planMap";
import BackLeftArrow from "~/images/back_arrow.svg?react";
import type { PlanType } from "~/interface/organization";
import type { RootState } from "~/store";
import PricingCard from "../pricingCard";
import styles from "./index.module.scss";

const planTitleToType: Record<string, PlanType> = {
  "\u30D5\u30EA\u30FC": "free",
  "\u30B9\u30BF\u30FC\u30BF\u30FC": "starter",
  "\u30B9\u30BF\u30F3\u30C0\u30FC\u30C9": "standard",
  "\u30D7\u30ED\u30D5\u30A7\u30C3\u30B7\u30E7\u30CA\u30EB": "professional",
};

interface SelectPlanProps {
  showHeader?: boolean;
}

const SelectPlan: React.FC<SelectPlanProps> = ({ showHeader = true }) => {
  const navigate = useNavigate();
  const organization = useSelector((state: RootState) => state.auth.organization);
  const [selected, setSelected] = useState("月払い");
  const [selectedTitle, setSelectedTitle] = useState<string | null>("スターター");

  if (!organization) return null;

  const currentPlanType = organization.plan.type;
  const currentPlanName = planMap[currentPlanType]?.name || currentPlanType;

  const isCurrentPlan = (planTitle: string) => {
    const planType = planTitleToType[planTitle];
    return planType === currentPlanType;
  };

  const handlePlanSelect = (planTitle: string) => {
    navigate(`/${organization.id}/billings/plan-change`, {
      state: { plan: planTitle },
    });
  };

  const freePlan = {
    title: "フリー",
    subtitle: "まずはここから",
    price: "0",
    features: [
      "プロジェクト作成可能数: 3",
      "利用可能ユーザー数: 3",
      "招待可能ゲスト数: 0",
      "生成クレジット: 150（初回のみ）",
      "アプリ生成利用回数: 3",
      "チャットリクエスト: 50",
      "サポートなし",
      "追加クレジット不可",
    ],
  };

  const plans = [
    {
      title: "スターター",
      subtitle: "少人数・個人での利用",
      price: "15,000",
      features: [
        "プロジェクト作成可能数: 3",
        "利用可能ユーザー数: 3",
        "招待可能ゲスト数: 0",
        "生成クレジット/月間: 100",
        "アプリ生成利用回数: 3",
        "チャットリクエスト/月間: 50",
        "初期費用なし",
        "オンラインサポート対応あり",
      ],
    },
    {
      title: "スタンダード",
      subtitle: "主要な機能を利用",
      price: "50,000",
      recommended: true,
      features: [
        "プロジェクト作成可能数: 10",
        "利用可能ユーザー数: 10",
        "招待可能ゲスト数: 10",
        "生成クレジット/月間: 500",
        "アプリ生成利用回数: 10",
        "チャットリクエスト/月間: 50",
        "初期費用：10万円",
        "導入コンサルティングを含む",
        "オンラインサポート対応あり",
      ],
    },
    {
      title: "プロフェッショナル",
      subtitle: "ビジネス利用に最適！",
      price: "150,000",
      features: [
        "プロジェクト作成可能数: 50",
        "利用可能ユーザー数: 20",
        "招待可能ゲスト数: 20",
        "生成クレジット/月間: 2,000",
        "アプリ生成利用回数: 30",
        "チャットリクエスト/月間: 50",
        "初期費用：10万円",
        "導入コンサルティングを含む",
        "オンラインサポート対応あり",
      ],
    },
  ];

  const calculatePrice = (price: string) => {
    if (price === "???") return price;
    if (selected === "年払い") {
      if (price === "0") return "0";
      if (price.includes("~")) return price;
      const monthlyPrice = parseInt(price.replace(/,/g, ""), 10);
      const yearlyPrice = monthlyPrice * 12;
      return yearlyPrice.toLocaleString();
    }
    return price;
  };

  return (
    <div className={styles["pageContainer"]}>
      {showHeader && (
        <div className={styles["headerContainer"]}>
          <TopSticky content="ステップ 4/ 4" />
          <div className={styles["header"]}>
            <BackLeftArrow />
            <h1>プラン選択</h1>
          </div>
        </div>
      )}
      <p className="text-sm text-gray-600 mb-2">
        現在のプラン: <span className="font-medium">{currentPlanName}</span>
      </p>
      <div
        className={`${styles["selectedPlanContainer"]} ${isCurrentPlan(freePlan.title) ? styles["selectedPlanContainer--current"] : ""}`}
      >
        <div className={styles["free-plan"]}>
          {isCurrentPlan(freePlan.title) && (
            <div className={styles["free-plan__currentBadge"]}>現在のプラン</div>
          )}
          <div className={styles["free-plan__nameRow"]}>
            <span className={styles["free-plan__label"]}>{freePlan.title}</span>
            <span className={styles["free-plan__note"]}>{freePlan.subtitle}</span>
          </div>
          <div className={styles["free-plan__line"]}>
            <span className={styles["free-plan__yen"]}>¥</span>
            <span className={styles["free-plan__price"]}>{calculatePrice(freePlan.price)}</span>
            <span className={styles["free-plan__period"]}>
              /{selected === "年払い" ? "年" : "月"}（税抜）
            </span>
          </div>
        </div>

        <div className={styles["selectedList"]}>
          {freePlan.features.map((feature, i) => (
            <PlanListItem key={i} text={feature} />
          ))}
        </div>

        <div className={styles["selectButton"]}>
          <Button
            label={isCurrentPlan(freePlan.title) ? "ご利用中" : "プランを変更する"}
            onClick={() => {
              if (!isCurrentPlan(freePlan.title)) {
                handlePlanSelect("フリー");
              }
            }}
            styleType={isCurrentPlan(freePlan.title) ? "current-plan" : "blue-selected-plan"}
            disabled={isCurrentPlan(freePlan.title)}
          />
        </div>
      </div>
      <div className={styles["planListContainer"]}>
        <ToggleSelector
          options={["月払い", "年払い"]}
          selected={selected}
          onChange={(val) => setSelected(val)}
        />

        <div className={styles["grid"]}>
          {plans.map((plan) => (
            <PricingCard
              key={plan.title}
              {...plan}
              price={calculatePrice(plan.price)}
              period={selected === "年払い" ? "年" : "月"}
              selected={selectedTitle === plan.title}
              isCurrentPlan={isCurrentPlan(plan.title)}
              onClick={() => {
                if (!isCurrentPlan(plan.title)) {
                  setSelectedTitle(plan.title);
                  handlePlanSelect(plan.title);
                }
              }}
            />
          ))}
        </div>
      </div>
      <p className={styles["price-note"]}>金額の表記はすべて税抜き価格です</p>
    </div>
  );
};

export default SelectPlan;
